import React, { useState } from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Skeleton,
  Paper,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import StarIcon from '@mui/icons-material/Star';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { formatCurrency } from '../../../../../utils/formatting';

// Define the Subscription interface
export interface SubscriptionTier {
  id: string;
  name: string;
  description: string;
  price: number;
  interval: 'monthly' | 'annual';
  features: string[];
  isPublic: boolean;
  position: number;
  maxMembers?: number;
  trialDays?: number;
  status: 'active' | 'archived' | 'draft';
  coverImageUrl?: string;
  benefits?: string[];
  created?: Date;
  modified?: Date;
}

// Props interface
interface SubscriptionSelectionStepProps {
  subscriptions: SubscriptionTier[];
  selectedSubscription: SubscriptionTier | null;
  selectedSubscriptions?: SubscriptionTier[];
  onSubscriptionSelect: (subscription: SubscriptionTier) => void;
  onMultipleSubscriptionsSelect?: (subscriptions: SubscriptionTier[]) => void;
  allowMultipleSelection?: boolean;
  loading: boolean;
}

const SubscriptionSelectionStep: React.FC<SubscriptionSelectionStepProps> = ({
  subscriptions,
  selectedSubscription,
  selectedSubscriptions = [],
  onSubscriptionSelect,
  onMultipleSubscriptionsSelect,
  allowMultipleSelection = false,
  loading
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Filter subscriptions based on search query
  const filteredSubscriptions = subscriptions.filter(subscription => {
    const query = searchQuery.toLowerCase();
    return (
      subscription.name.toLowerCase().includes(query) ||
      subscription.description.toLowerCase().includes(query) ||
      subscription.features.some(feature => feature.toLowerCase().includes(query))
    );
  });

  // Get the status color
  const getStatusColor = (status?: string) => {
    if (!status) return 'default';
    
    switch (status) {
      case 'active':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'error';
      default:
        return 'default';
    }
  };

  // Format price with currency
  const formatPrice = (price: number, interval: 'monthly' | 'annual') => {
    return `$${price.toFixed(2)}/${interval === 'annual' ? 'year' : 'month'}`;
  };

  // Check if all subscriptions are selected
  const allSelected = allowMultipleSelection && 
    selectedSubscriptions.length === subscriptions.length && 
    subscriptions.length > 0;

  // Handle select all subscriptions
  const handleSelectAll = () => {
    if (onMultipleSubscriptionsSelect) {
      if (allSelected) {
        // Deselect all
        onMultipleSubscriptionsSelect([]);
      } else {
        // Select all
        onMultipleSubscriptionsSelect([...subscriptions]);
      }
    }
  };

  // Check if a subscription is selected
  const isSelected = (subscription: SubscriptionTier) => {
    if (allowMultipleSelection) {
      return selectedSubscriptions.some(selected => selected.id === subscription.id);
    }
    return selectedSubscription?.id === subscription.id;
  };

  // Handle individual subscription selection
  const handleSubscriptionSelect = (subscription: SubscriptionTier) => {
    if (allowMultipleSelection && onMultipleSubscriptionsSelect) {
      if (isSelected(subscription)) {
        // Remove from selection
        onMultipleSubscriptionsSelect(
          selectedSubscriptions.filter(selected => selected.id !== subscription.id)
        );
      } else {
        // Add to selection
        onMultipleSubscriptionsSelect([...selectedSubscriptions, subscription]);
      }
    } else {
      onSubscriptionSelect(subscription);
    }
  };

  // Render loading skeletons
  const renderSkeletons = () => {
    return Array(3).fill(0).map((_, index) => (
      <Grid item xs={12} sm={6} md={4} key={`skeleton-${index}`}>
        <Card>
          <Skeleton variant="rectangular" height={140} />
          <CardContent>
            <Skeleton variant="text" height={30} />
            <Skeleton variant="text" height={20} width="60%" />
            <Skeleton variant="text" height={20} width="75%" />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Skeleton variant="rectangular" width={80} height={30} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    ));
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select a Subscription
      </Typography>
      
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search subscriptions by name, description, or features..."
        margin="normal"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />
      
      {allowMultipleSelection && (
        <Box sx={{ mb: 2 }}>
          <Paper
            sx={{
              p: 2,
              border: allSelected ? '2px solid #3f51b5' : '1px solid #e0e0e0',
              cursor: 'pointer',
              boxShadow: allSelected ? '0 0 10px rgba(63, 81, 181, 0.3)' : 'none',
              backgroundColor: allSelected ? 'rgba(63, 81, 181, 0.05)' : 'white',
              '&:hover': {
                boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
              }
            }}
            onClick={handleSelectAll}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ 
                width: 24, 
                height: 24, 
                borderRadius: '50%', 
                bgcolor: allSelected ? 'primary.main' : 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2
              }}>
                {allSelected && <CheckIcon fontSize="small" sx={{ color: 'white' }} />}
              </Box>
              <Typography variant="subtitle1" fontWeight="medium">
                {allSelected ? 'All Subscriptions Selected' : 'Select All Subscriptions'}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 6 }}>
              {allSelected 
                ? 'All subscription details will be included in the email template' 
                : 'Include all subscription options in the email for recipients to choose from'}
            </Typography>
          </Paper>
        </Box>
      )}
      
      {loading ? (
        <Grid container spacing={3}>
          {renderSkeletons()}
        </Grid>
      ) : filteredSubscriptions.length > 0 ? (
        <Grid container spacing={3}>
          {filteredSubscriptions.map((subscription) => (
            <Grid item xs={12} sm={6} md={4} key={subscription.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: isSelected(subscription) ? '2px solid #3f51b5' : 'none',
                  boxShadow: isSelected(subscription) ? '0 0 10px rgba(63, 81, 181, 0.5)' : 'inherit'
                }}
              >
                <CardActionArea onClick={() => handleSubscriptionSelect(subscription)}>
                  <CardMedia
                    component="img"
                    height="180"
                    image={subscription.coverImageUrl || `https://source.unsplash.com/featured/?subscription,${encodeURIComponent(subscription.name)}`}
                    alt={subscription.name}
                    sx={{ 
                      objectFit: 'cover',
                      objectPosition: 'center',
                      boxShadow: 'inset 0 -40px 40px -30px rgba(0,0,0,0.5)',
                      filter: 'brightness(1.05) contrast(1.05)'
                    }}
                  />
                  <Chip 
                    label={subscription.status}
                    color={getStatusColor(subscription.status) as any}
                    size="small"
                    sx={{ 
                      position: 'absolute', 
                      top: 10, 
                      right: 10,
                      textTransform: 'capitalize',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="div" gutterBottom>
                      {subscription.name}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {subscription.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <AttachMoneyIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
                      <Typography variant="h6" color="primary.main" fontWeight="bold">
                        {formatPrice(subscription.price, subscription.interval)}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      Key Features:
                    </Typography>
                    
                    <List dense disablePadding>
                      {subscription.features.slice(0, 3).map((feature, index) => (
                        <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <CheckIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                      {subscription.features.length > 3 && (
                        <ListItem disablePadding sx={{ py: 0.5 }}>
                          <ListItemText 
                            primary={`+ ${subscription.features.length - 3} more features`}
                            sx={{ color: 'text.secondary', fontStyle: 'italic', pl: 3 }}
                          />
                        </ListItem>
                      )}
                    </List>
                    
                    {subscription.trialDays && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {subscription.trialDays}-day free trial
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No subscriptions found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No active subscription plans were found. Please create subscription plans in the Subscriptions section.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default SubscriptionSelectionStep;
